import { useMemo } from "react";

import { useLocalStorage } from "usehooks-ts";

import { createFNVHashSync } from "../../../components/authentication/utils";

import { DEFAULT_SIDEBAR_STATE } from "./sidebar.constants";

import type { SidebarTileId } from "./sidebar.constants";
import type { SidebarState } from "./sidebar.types";

/**
 * Generate the localStorage key for a specific user's sidebar state.
 *
 * @param userId - Unique user identifier
 * @returns localStorage key, formatted as `sidebar_{hashedUserId}`
 */
export const getSidebarStorageKey = (userId: string): string => {
  const hashedId = createFNVHashSync(userId);
  return `sidebar_${hashedId}`;
};

/**
 * Hook to manage sidebar state for a specific user with localStorage
 * persistence. State is reactive and persists across navigations and browser
 * sessions. Each user gets isolated state keyed by their hashed user ID.
 *
 * Use `sidebarActions` to produce updated state, e.g.
 * `setSidebarState(prev => sidebarActions.toggleCollapsed(prev))`.
 *
 * @param userId - Unique identifier for the current user
 * @returns Tuple of [state, setState, removeValue]
 */
export const useSidebarStorage = (userId: string) => {
  const storageKey = useMemo(() => getSidebarStorageKey(userId), [userId]);

  const [state, setState, removeValue] = useLocalStorage<SidebarState>(
    storageKey,
    DEFAULT_SIDEBAR_STATE
  );

  return [state, setState, removeValue] as const;
};

/**
 * Utility functions for common sidebar state operations
 */
export const sidebarActions = {
  toggleCollapsed: (state: SidebarState): SidebarState => ({
    ...state,
    isCollapsed: !state.isCollapsed,
  }),
  dismissTile: (state: SidebarState, tileId: SidebarTileId): SidebarState => ({
    ...state,
    dismissedTileIds: {
      ...state.dismissedTileIds,
      [tileId]: Date.now(),
    },
  }),
  undismissTile: (state: SidebarState, tileId: SidebarTileId): SidebarState => {
    const { [tileId]: _, ...remainingTiles } = state.dismissedTileIds;
    return {
      ...state,
      dismissedTileIds: remainingTiles,
    };
  },
  isTileDismissed: (state: SidebarState, tileId: SidebarTileId): boolean => {
    return tileId in state.dismissedTileIds;
  },
  getTileDismissedAt: (
    state: SidebarState,
    tileId: SidebarTileId
  ): number | null => {
    return state.dismissedTileIds[tileId] ?? null;
  },
  toggleMenuGroup: (state: SidebarState, menuId: string): SidebarState => ({
    ...state,
    expandedMenuIds: state.expandedMenuIds.includes(menuId)
      ? state.expandedMenuIds.filter((id) => id !== menuId)
      : [...state.expandedMenuIds, menuId],
  }),
  expandMenuGroup: (state: SidebarState, menuId: string): SidebarState => ({
    ...state,
    expandedMenuIds: state.expandedMenuIds.includes(menuId)
      ? state.expandedMenuIds
      : [...state.expandedMenuIds, menuId],
  }),
  collapseMenuGroup: (state: SidebarState, menuId: string): SidebarState => ({
    ...state,
    expandedMenuIds: state.expandedMenuIds.filter((id) => id !== menuId),
  }),
  collapseAllMenuGroups: (state: SidebarState): SidebarState => ({
    ...state,
    expandedMenuIds: [],
  }),
  isMenuGroupExpanded: (state: SidebarState, menuId: string): boolean => {
    return state.expandedMenuIds.includes(menuId);
  },
  reset: (): SidebarState => ({ ...DEFAULT_SIDEBAR_STATE }),
};
